import apiService from "./apiService";

const TOKEN_KEY = "sgcm_token";
const USER_KEY = "sgcm_user";

export const authService = {
  // Iniciar sesión
  login: async (email, password) => {
    try {
      const response = await apiService.post("/auth/login", {
        email,
        password,
      });

      // El backend devuelve: { status: "success", token, data: { user } }
      if (response.status === "success" && response.token && response.data?.user) {
        // Guardar token y datos del usuario
        localStorage.setItem(TOKEN_KEY, response.token);
        localStorage.setItem(USER_KEY, JSON.stringify(response.data.user));

        return {
          success: true,
          user: response.data.user,
          token: response.token,
        };
      }

      throw new Error(response.message || "Error en el login");
    } catch (error) {
      throw error;
    }
  },

  // Cerrar sesión
  logout: async () => {
    try {
      // Llamar al endpoint de logout si existe
      await apiService.post("/auth/logout");
    } catch (error) {
      // Continuar con logout local aunque falle el servidor
      console.warn("Error al hacer logout en servidor:", error);
    } finally {
      // Limpiar datos locales
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    }
  },

  // Registrar nuevo usuario (solo admin)
  register: async (userData) => {
    try {
      const response = await apiService.post("/auth/register", userData);

      if (response.success) {
        return response.data;
      }

      throw new Error(response.message || "Error en el registro");
    } catch (error) {
      throw error;
    }
  },

  // Renovar token
  refreshToken: async () => {
    try {
      const response = await apiService.post("/auth/refresh");

      if (response.success && response.data.token) {
        localStorage.setItem(TOKEN_KEY, response.data.token);
        return response.data.token;
      }

      throw new Error("Error al renovar token");
    } catch (error) {
      // Si falla la renovación, hacer logout
      authService.logout();
      throw error;
    }
  },

  // Obtener información del usuario actual
  getCurrentUser: async () => {
    try {
      const response = await apiService.get("/auth/me");

      if (response.success) {
        // Actualizar datos del usuario en localStorage
        localStorage.setItem(USER_KEY, JSON.stringify(response.data));
        return response.data;
      }

      throw new Error("Error al obtener datos del usuario");
    } catch (error) {
      throw error;
    }
  },

  // Obtener token del localStorage
  getToken: () => {
    return localStorage.getItem(TOKEN_KEY);
  },

  // Obtener usuario del localStorage
  getStoredUser: () => {
    const userData = localStorage.getItem(USER_KEY);
    return userData ? JSON.parse(userData) : null;
  },

  // Verificar si está autenticado
  isAuthenticated: () => {
    const token = authService.getToken();
    const user = authService.getStoredUser();
    return !!(token && user);
  },

  // Verificar si el usuario tiene un rol específico
  hasRole: (role) => {
    const user = authService.getStoredUser();
    return user && user.role === role;
  },

  // Verificar si es administrador
  isAdmin: () => {
    return authService.hasRole("admin");
  },

  // Verificar si es viewer
  isViewer: () => {
    return authService.hasRole("viewer");
  },

  // Cambiar contraseña
  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await apiService.put("/auth/change-password", {
        currentPassword,
        newPassword,
      });

      if (response.success) {
        return true;
      }

      throw new Error(response.message || "Error al cambiar contraseña");
    } catch (error) {
      throw error;
    }
  },

  // Decodificar token JWT (básico, sin verificación)
  decodeToken: (token) => {
    try {
      const payload = token.split(".")[1];
      const decoded = JSON.parse(atob(payload));
      return decoded;
    } catch (error) {
      return null;
    }
  },

  // Verificar si el token está próximo a expirar
  isTokenExpiringSoon: (minutesThreshold = 5) => {
    const token = authService.getToken();
    if (!token) return true;

    const decoded = authService.decodeToken(token);
    if (!decoded || !decoded.exp) return true;

    const now = Date.now() / 1000;
    const timeUntilExpiry = decoded.exp - now;
    const minutesUntilExpiry = timeUntilExpiry / 60;

    return minutesUntilExpiry < minutesThreshold;
  },
};

export default authService;
